import { useEffect, useState } from 'react';
import { MdDownload, MdInsertDriveFile } from "react-icons/md";
import { useStrings } from '../i18n/useStrings';
import { util } from "../helpers/common";
import { formatFileSize, handleFileDownload } from '../helpers/fileUtils';
import ImageLightbox from './ImageLightbox';
import './file-message.css';

/**
 * File message component
 * Displays file attachments in conversation (sent or received)
 *
 * @param {Object} conversationEntry - Message entry with file attachment
 */
export default function FileMessage({ conversationEntry }) {
    const strings = useStrings();
    const [showLightbox, setShowLightbox] = useState(false);

    // Cleanup blob URLs when component unmounts to prevent memory leaks
    useEffect(() => {
        return () => {
            // Revoke any blob URLs when component unmounts
            const attachments = conversationEntry.attachments;
            if (attachments && attachments.length > 0) {
                attachments.forEach(attachment => {
                    const url = attachment.url || attachment.referenceId;
                    if (typeof url === 'string' && url.startsWith('blob:')) {
                        URL.revokeObjectURL(url);
                    }
                });
            }
        };
    }, [conversationEntry]);

    // Extract attachment from entry
    const attachments = conversationEntry.attachments;
    
    if (!attachments || attachments.length === 0) {
        return null;
    }

    const attachment = attachments[0];
    // Agent-sent images use type "Image" (Salesforce format) without a mimeType field
    const isImage = attachment.mimeType?.startsWith('image/') || attachment.type === 'Image';
    // Agent attachments may use referenceId instead of url
    const attachmentUrl = attachment.url || attachment.referenceId;
    const isEndUserMessage = conversationEntry.isEndUserMessage;
    const fileMessageText = conversationEntry.content?.staticContent?.text || '';

    /**
     * Generate classname for file message bubble
     */
    function generateMessageBubbleClassName() {
        return `fileMessageBubble ${isEndUserMessage ? 'outgoing' : 'incoming'}`;
    }

    /**
     * Generate message sender content
     */
    function generateMessageSenderContentText() {
        const formattedTime = util.getFormattedTime(conversationEntry.transcriptedTimestamp);
        return `${isEndUserMessage ? strings.you : conversationEntry.actorName} ${strings.at} ${formattedTime}`;
    }

    /**
     * Handle download click for both blob and regular URLs
     */
    function handleDownloadClick(e) {
        e.preventDefault();
        handleFileDownload(attachmentUrl, attachment.name);
    }

    /**open in lightbox overlay
     */
    function handleImageClick(e) {
        e.preventDefault();
        setShowLightbox(true);
        // For regular URLs, let the default behavior work (target="_blank")
    }

    return (
        <>
            <p className={`textMessageSenderContent ${isEndUserMessage ? 'outgoing' : 'incoming'}`}>
                {generateMessageSenderContentText()}
            </p>

            <div className={generateMessageBubbleClassName()}>
                {isImage ? (
                    <div className="fileMessageImageContainer">
                        <a 
                            href={attachmentUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="fileMessageImageLink"
                            title={strings.clickToEnlarge || 'Click to enlarge'}
                            onClick={handleImageClick}
                        >
                            <img
                                src={attachmentUrl}
                                alt={attachment.name || strings.attachment || 'Shared image attachment'}
                                className="fileMessageImage"
                            />
                        </a>
                        <div className="fileMessageImageFooter">
                            {attachment.name && (
                                <div className="fileMessageImageCaption">
                                    {attachment.name}
                                    {attachment.size && ` • ${formatFileSize(attachment.size)}`}
                                </div>
                            )}
                            {attachmentUrl && (
                                <a
                                    href={attachmentUrl}
                                    download={attachment.name}
                                    onClick={handleDownloadClick}
                                    className="fileMessageImageDownload"
                                    title={strings.download || 'Download'}
                                >
                                    <MdDownload size={18} />
                                </a>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="fileMessageDocument">
                        <div className="fileMessageDocIcon">
                            <MdInsertDriveFile size={32} />
                        </div>
                        <div className="fileMessageDocInfo">
                            <div className="fileMessageDocName">
                                {attachment.name || strings.attachment}
                            </div>
                            {attachment.size && (
                                <div className="fileMessageDocSize">
                                    {formatFileSize(attachment.size)}
                                </div>
                            )}
                        </div>
                        {attachmentUrl && (
                            <a
                                href={attachmentUrl}
                                download={attachment.name}
                                onClick={handleDownloadClick}
                                className="fileMessageDocDownload"
                                title={strings.download}
                            >
                                <MdDownload size={20} />
                            </a>
                        )}
                    </div>
                )}

                {/* Show text content if present (description/caption) */}
                {fileMessageText && (
                    <div className="fileMessageText">
                        {fileMessageText}
                    </div>
                )}
            </div>

            {/* Image lightbox overlay */}
            {showLightbox && isImage && (
                <ImageLightbox
                    imageUrl={attachmentUrl}
                    imageName={attachment.name}
                    onClose={() => setShowLightbox(false)}
                />
            )}
        </>
    );
}
